import { defineMessages, type IntlShape, type MessageDescriptor } from "react-intl";

export type ImageFitValue = "cover" | "contain" | "fill";

export type ImageFitOption = {
  value: ImageFitValue;
  label: MessageDescriptor;
  shortDescription: MessageDescriptor;
  detail: MessageDescriptor;
  ariaLabel: MessageDescriptor;
};

export type LocalizedImageFitOption = {
  value: ImageFitValue;
  label: string;
  shortDescription: string;
  detail: string;
  ariaLabel: string;
};

const imageFitMessages = defineMessages({
  coverLabel: {
    id: "imageFit.cover.label",
    defaultMessage: "Cover",
  },
  coverShortDescription: {
    id: "imageFit.cover.shortDescription",
    defaultMessage: "Crops edges",
  },
  coverDetail: {
    id: "imageFit.cover.detail",
    defaultMessage: "Cover fills the frame and crops anything outside it.",
  },
  coverAriaLabel: {
    id: "imageFit.cover.ariaLabel",
    defaultMessage: "Cover: fills the frame and crops edges",
  },
  containLabel: {
    id: "imageFit.contain.label",
    defaultMessage: "Contain",
  },
  containShortDescription: {
    id: "imageFit.contain.shortDescription",
    defaultMessage: "Shows all",
  },
  containDetail: {
    id: "imageFit.contain.detail",
    defaultMessage: "Contain keeps the full image visible and may leave empty space.",
  },
  containAriaLabel: {
    id: "imageFit.contain.ariaLabel",
    defaultMessage: "Contain: shows the full image and keeps its proportions",
  },
  fillLabel: {
    id: "imageFit.fill.label",
    defaultMessage: "Stretch",
  },
  fillShortDescription: {
    id: "imageFit.fill.shortDescription",
    defaultMessage: "May distort",
  },
  fillDetail: {
    id: "imageFit.fill.detail",
    defaultMessage: "Stretch forces the image into the frame and can distort it.",
  },
  fillAriaLabel: {
    id: "imageFit.fill.ariaLabel",
    defaultMessage: "Stretch: forces the image into the frame and may distort it",
  },
});

export const IMAGE_FIT_OPTIONS: ImageFitOption[] = [
  {
    value: "cover",
    label: imageFitMessages.coverLabel,
    shortDescription: imageFitMessages.coverShortDescription,
    detail: imageFitMessages.coverDetail,
    ariaLabel: imageFitMessages.coverAriaLabel,
  },
  {
    value: "contain",
    label: imageFitMessages.containLabel,
    shortDescription: imageFitMessages.containShortDescription,
    detail: imageFitMessages.containDetail,
    ariaLabel: imageFitMessages.containAriaLabel,
  },
  {
    value: "fill",
    label: imageFitMessages.fillLabel,
    shortDescription: imageFitMessages.fillShortDescription,
    detail: imageFitMessages.fillDetail,
    ariaLabel: imageFitMessages.fillAriaLabel,
  },
];

export const getImageFitOption = (value: ImageFitValue): ImageFitOption =>
  IMAGE_FIT_OPTIONS.find((option) => option.value === value) ?? IMAGE_FIT_OPTIONS[0]!;

export const formatImageFitOption = (
  intl: IntlShape,
  option: ImageFitOption,
): LocalizedImageFitOption => ({
  value: option.value,
  label: intl.formatMessage(option.label),
  shortDescription: intl.formatMessage(option.shortDescription),
  detail: intl.formatMessage(option.detail),
  ariaLabel: intl.formatMessage(option.ariaLabel),
});
